import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { KeyRound, LockOpen, Pencil, UserMinus, UserRoundPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Badge, Card, Modal, PageHeader, btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

const FORM_VACIO = {
  legajo: "", nombre: "", apellido: "", fecha_alta: "", local_habitual_id: "",
  horario_entrada: "10:00", horario_salida: "19:00", hora_adicional_desde: "18:00", pin: "1234",
};

export default function EmpleadosPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const esLiq = user?.rol === "liquidacion";
  const [editando, setEditando] = useState(null); // null | "nuevo" | empleado
  const [form, setForm] = useState(FORM_VACIO);
  const [baja, setBaja] = useState(null); // empleado
  const [fechaBaja, setFechaBaja] = useState("");

  const { data: locales } = useQuery({ queryKey: ["locales"], queryFn: async () => (await api.get("/locales")).data });
  const { data: empleados, isLoading } = useQuery({
    queryKey: ["empleados"],
    queryFn: async () => (await api.get("/empleados")).data,
    refetchInterval: 5000,
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ["empleados"] });

  const mutGuardar = useMutation({
    mutationFn: async () => (editando === "nuevo"
      ? api.post("/rrhh/empleados", form)
      : api.put(`/rrhh/empleados/${editando.id}`, form)),
    onSuccess: () => { invalidar(); setEditando(null); toast.success(editando === "nuevo" ? "Empleado dado de alta" : "Empleado actualizado"); },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutAccion = useMutation({
    mutationFn: async ({ id, accion, body }) => api.post(`/rrhh/empleados/${id}/${accion}`, body || {}),
    onSuccess: (_r, v) => {
      invalidar();
      setBaja(null);
      toast.success(v.accion === "resetear-pin" ? "PIN reseteado a 1234 y desbloqueado"
        : v.accion === "desbloquear-pin" ? "PIN desbloqueado" : "Empleado dado de baja");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const abrirEditar = (e) => {
    setForm({
      legajo: e.legajo, nombre: e.nombre, apellido: e.apellido, fecha_alta: e.fecha_alta,
      local_habitual_id: e.local_habitual_id, horario_entrada: e.horario_entrada,
      horario_salida: e.horario_salida, hora_adicional_desde: e.hora_adicional_desde, pin: "1234",
    });
    setEditando(e);
  };

  const campo = (id, label, tipo = "text") => (
    <div key={id}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</label>
      <input
        type={tipo}
        value={form[id]}
        onChange={(e) => setForm({ ...form, [id]: e.target.value })}
        className={`${inputCls} w-full`}
        data-testid={`emp-${id}`}
      />
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-empleados">
      <PageHeader
        titulo="Empleados"
        subtitulo="El plantel de los tres locales: altas, bajas y PIN. Tocá una fila para abrir la ficha completa."
        testid="header-empleados"
      >
        {!esLiq && (
          <button className={`${btnPrim} flex items-center gap-2`} onClick={() => { setForm(FORM_VACIO); setEditando("nuevo"); }} data-testid="btn-nuevo-empleado">
            <UserRoundPlus size={15} /> Nuevo empleado
          </button>
        )}
      </PageHeader>

      <Card testid="card-empleados" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Empleado</th>
                <th className="px-4 py-3 font-semibold">Local habitual</th>
                <th className="px-4 py-3 font-semibold">Alta</th>
                <th className="px-4 py-3 font-semibold">Horario</th>
                <th className="px-4 py-3 font-semibold">PIN</th>
                <th className="px-4 py-3 font-semibold">Activo</th>
                <th className="px-5 py-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Cargando…</td></tr>}
              {empleados?.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => navigate(esLiq ? `/liquidacion/persona/${e.id}` : `/rrhh/empleados/${e.id}`)}
                  className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-[#2DD4BF]/[0.04]"
                  data-testid={`empleado-${e.legajo}`}
                >
                  <td className="min-w-44 px-5 py-3">
                    <span className="flex items-center gap-3">
                      <Avatar iniciales={`${e.nombre[0]}${e.apellido[0]}`.toUpperCase()} size="sm" />
                      <span className="whitespace-nowrap leading-tight">
                        <span className="block text-sm font-semibold text-white">{e.apellido}, {e.nombre}</span>
                        <span className="block font-mono-num text-[11px] text-gray-500">Legajo {e.legajo}</span>
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{e.local_habitual}</td>
                  <td className="px-4 py-3 font-mono-num text-gray-400">{fmtFecha(e.fecha_alta)}</td>
                  <td className="px-4 py-3 font-mono-num text-gray-400">{e.horario_entrada} a {e.horario_salida}</td>
                  <td className="px-4 py-3">
                    <Badge color={e.pin_bloqueado ? "rojo" : "verde"} testid={`pin-${e.legajo}`}>{e.pin_bloqueado ? "Bloqueado" : "Activo"}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={e.activo ? "gris" : "rojo"}>{e.activo ? "Sí" : `Baja ${fmtFecha(e.fecha_baja)}`}</Badge>
                  </td>
                  <td className="px-5 py-3" onClick={(ev) => ev.stopPropagation()}>
                    {!esLiq && (
                      <span className="flex gap-1">
                        <button title="Editar" className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/5 hover:text-white" onClick={() => abrirEditar(e)} data-testid={`editar-${e.legajo}`}><Pencil size={15} /></button>
                        <button title="Resetear PIN a 1234" className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/5 hover:text-amber-300" onClick={() => mutAccion.mutate({ id: e.id, accion: "resetear-pin" })} data-testid={`resetear-${e.legajo}`}><KeyRound size={15} /></button>
                        {e.pin_bloqueado && (
                          <button title="Desbloquear PIN" className="rounded-lg p-1.5 text-red-300 transition-colors hover:bg-white/5" onClick={() => mutAccion.mutate({ id: e.id, accion: "desbloquear-pin" })} data-testid={`desbloquear-${e.legajo}`}><LockOpen size={15} /></button>
                        )}
                        {e.activo && (
                          <button title="Dar de baja" className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/5 hover:text-red-300" onClick={() => { setBaja(e); setFechaBaja(new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" })); }} data-testid={`baja-${e.legajo}`}><UserMinus size={15} /></button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {empleados?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center">
                    <Users size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">No hay empleados</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="border-t border-white/8 px-5 py-3 text-xs text-gray-500">
          {empleados?.length ?? "…"} empleados
        </footer>
      </Card>

      {editando && (
        <Modal titulo={editando === "nuevo" ? "Alta de empleado" : `Editar — ${form.apellido}, ${form.nombre}`} onClose={() => setEditando(null)} testid="modal-empleado">
          <div className="grid grid-cols-2 gap-3.5">
            {campo("legajo", "Legajo (4 dígitos)")}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Local habitual</label>
              <select value={form.local_habitual_id} onChange={(e) => setForm({ ...form, local_habitual_id: e.target.value })} className={`${inputCls} w-full`} data-testid="emp-local">
                <option value="">Elegí el local…</option>
                {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </div>
            {campo("nombre", "Nombre")}
            {campo("apellido", "Apellido")}
            {campo("fecha_alta", "Fecha de alta", "date")}
            {editando === "nuevo" && campo("pin", "PIN inicial")}
            {campo("horario_entrada", "Entrada", "time")}
            {campo("horario_salida", "Salida", "time")}
            {campo("hora_adicional_desde", "Adicional desde", "time")}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button className={btnSec} onClick={() => setEditando(null)}>Cancelar</button>
            <button className={btnPrim} disabled={mutGuardar.isPending} onClick={() => mutGuardar.mutate()} data-testid="emp-guardar">Guardar</button>
          </div>
        </Modal>
      )}

      {baja && (
        <Modal titulo={`Dar de baja — ${baja.apellido}, ${baja.nombre}`} onClose={() => setBaja(null)} testid="modal-baja">
          <p className="mb-4 text-sm text-gray-400">No puede fichar desde la fecha de baja. El historial queda guardado.</p>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fecha de baja</label>
          <input type="date" value={fechaBaja} onChange={(e) => setFechaBaja(e.target.value)} className={`${inputCls} w-full`} data-testid="baja-fecha" />
          <div className="mt-5 flex justify-end gap-2">
            <button className={btnSec} onClick={() => setBaja(null)}>Cancelar</button>
            <button
              className="rounded-xl bg-red-400 px-4 py-2 text-sm font-bold text-black transition-all hover:brightness-110 disabled:opacity-40"
              disabled={mutAccion.isPending || !fechaBaja}
              onClick={() => mutAccion.mutate({ id: baja.id, accion: "baja", body: { fecha: fechaBaja } })}
              data-testid="baja-confirmar"
            >
              Dar de baja
            </button>
          </div>
        </Modal>
      )}
    </motion.div>
  );
}
